import { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import ContentFile from '@/components/atom/icon/ContentFile';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import { tokenState } from '@/recoil/atoms/common/auth';
import { ROUTES } from '@/types/common';

interface Props {
  isMypage?: boolean;
  isSearchpage?: boolean;
}
const EmptyPostList = ({ isMypage = false, isSearchpage = false }: Props) => {
  const token = useRecoilValue(tokenState);
  const { setShowAuthModal } = useAuthModal();
  const { moveToPage } = useCustomRouter();

  const [exceptionSpace, setExceptionSpace] = useState<number>(0);

  const onClickWrite = () => {
    if (!token) {
      setShowAuthModal(true);
    } else {
      moveToPage({
        webLink: ROUTES.COMMUNITY.WRITE.path,
        mobileScreenName: ROUTES.COMMUNITY.WRITE.appScreenName,
      });
    }
  };

  const getContentTitle = useCallback(() => {
    if (isMypage) {
      return '작성글이 없어요';
    }
    if (isSearchpage) {
      return '검색 결과가 없어요';
    }
    return '게시글이 없어요';
  }, [isMypage, isSearchpage]);

  useEffect(() => {
    const appHeaderHeight = document.getElementById('appHeader')?.clientHeight || 0;
    const fixedBottomSheetHeight = document.getElementById('FixedBottomSheet')?.clientHeight || 0;
    setExceptionSpace(appHeaderHeight + fixedBottomSheetHeight);
  }, []);

  return (
    <Wrapper exceptionSpace={exceptionSpace}>
      <Body>
        <ContentFile />
        <Content>
          {getContentTitle()}
          <p>{isSearchpage ? '다른 검색어를 입력해주세요.' : '첫 게시글을 작성해보세요.'}</p>
        </Content>
        {!isSearchpage && <WriteButton onClick={onClickWrite}>글쓰러 가기</WriteButton>}
      </Body>
    </Wrapper>
  );
};

export default EmptyPostList;

const Wrapper = styled.div<{ exceptionSpace: number }>`
  ${({ theme, exceptionSpace }) => `
    background: ${theme.color.white};
    height: calc(100vh - ${exceptionSpace}px);
  `}
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  svg {
    margin-bottom: 20px;
  }
`;

const Content = styled.div`
  text-align: center;

  ${({ theme }) => `
    ${theme.typography.UI20SB};
    color: ${theme.color.gray700};

    p {
      ${theme.typography.UI14M};
      color: ${theme.color.gray400};
      margin-top: 8px;
    }
  `};
`;

const WriteButton = styled(DefaultButtonStyled)`
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 20px;

  ${({ theme }) => `
    ${theme.typography.UI16SB};
    color: ${theme.color.white};
    background: ${theme.color.primary500};
    
    &: active {
      background: ${theme.color.primary700};
    }
  `};
`;
